import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { paquetesApi } from '../api/paquetes.js';
import type {
  RegistrarPaqueteRequest,
  RetirarPaqueteRequest,
  DevolverPaqueteRequest,
  AnularPaqueteRequest,
} from '@condo/contracts';

const qk = {
  pendientes: ['paquetes', 'pendientes'] as const,
  listar: (query?: unknown) => ['paquetes', 'listar', query] as const,
  mis: ['paquetes', 'mis'] as const,
  detail: (id: string) => ['paquetes', id] as const,
  unidad: (unidadId: string) => ['paquetes', 'unidad', unidadId] as const,
};

export function usePaquetesPendientes() {
  return useQuery({
    queryKey: qk.pendientes,
    queryFn: async () => {
      const resp = await paquetesApi.listarPendientes();
      return resp.items;
    },
    staleTime: 30_000,
    refetchInterval: 30_000,
  });
}

export function usePaquetesListar(query?: { estado?: string; unidadId?: string }) {
  return useQuery({
    queryKey: qk.listar(query),
    queryFn: async () => {
      const resp = await paquetesApi.listar(query);
      return resp.items;
    },
    staleTime: 30_000,
  });
}

export function useMisPaquetes() {
  return useQuery({
    queryKey: qk.mis,
    queryFn: async () => {
      const resp = await paquetesApi.listarMis();
      return resp.items;
    },
    staleTime: 30_000,
  });
}

export function usePaqueteDetail(id: string | undefined) {
  return useQuery({
    queryKey: qk.detail(id ?? ''),
    queryFn: () => paquetesApi.getById(id!),
    enabled: !!id,
    staleTime: 30_000,
  });
}

export function useHistorialPaquetesUnidad(unidadId: string | undefined) {
  return useQuery({
    queryKey: qk.unidad(unidadId ?? ''),
    queryFn: async () => {
      const resp = await paquetesApi.historialUnidad(unidadId!);
      return resp.items;
    },
    enabled: !!unidadId,
    staleTime: 60_000,
  });
}

export function useRegistrarPaquete() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: RegistrarPaqueteRequest) => paquetesApi.registrar(body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['paquetes'] });
    },
  });
}

export function useRetirarPaquete() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { id: string; body: RetirarPaqueteRequest }) =>
      paquetesApi.retirar(v.id, v.body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['paquetes'] }),
  });
}

export function useDevolverPaquete() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { id: string; body: DevolverPaqueteRequest }) =>
      paquetesApi.devolver(v.id, v.body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['paquetes'] }),
  });
}

export function useAnularPaquete() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { id: string; body: AnularPaqueteRequest }) =>
      paquetesApi.anular(v.id, v.body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['paquetes'] }),
  });
}
